import { useEffect, useState } from "react";
import useMediaQuery from "../hooks/useMediaQuery";
import { motion } from "framer-motion";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import { getLenisInstance, onLenisInstanceChange } from "../lenisInstance";
import { useRevealed } from "../revealContext";

/* THE ROLL-UP LABEL SHARED BY EVERY NAV ENTRY */
const NavLabel = ({ page }) => (
  <motion.a
    whileHover="onHover"
    initial="start"
    whileTap="onClick"
    className="relative block overflow-hidden whitespace-nowrap"
  >
    <motion.div
      variants={{
        start: { y: 0 },
        onHover: { y: "-100%", transition: { duration: 0.3 } },
      }}
    >
      {page}
    </motion.div>
    <motion.div
      variants={{
        onClick: { scale: 0.8 },
        start: { y: "100%" },
        onHover: { y: 0, transition: { duration: 0.3 } },
      }}
      className="absolute inset-0"
    >
      {page}
    </motion.div>
  </motion.a>
);

/*ANCHOR LINKS*/
const Link = ({ page, selectedPage, setSelectedPage }) => {
  const lowerCasePage = page.toLowerCase();
  const location = useLocation();
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.preventDefault();
    setSelectedPage(lowerCasePage);

    // Anchors only exist on the landing route - from anywhere else, go there
    // first and let the router scroll to the section once it has painted.
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: lowerCasePage } });
      return;
    }

    const lenis = getLenisInstance();
    // Go to home page(landing)
    if (lowerCasePage === "home") {
      if (lenis) {
        lenis.scrollTo("top");
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    if (lenis) {
      lenis.scrollTo(`#${lowerCasePage}`);
    } else {
      document
        .getElementById(lowerCasePage)
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  /* ANIMATIONS */
  return (
    <a
      className={`${selectedPage === lowerCasePage ? "text-blue" : ""}`}
      href={`#${lowerCasePage}`}
      onClick={handleClick}
    >
      <NavLabel page={page} />
    </a>
  );
};

/*ROUTE LINKS*/
const PageLink = ({ page, to, selectedPage, setSelectedPage }) => {
  const lowerCasePage = page.toLowerCase();

  return (
    <RouterLink
      className={`${selectedPage === lowerCasePage ? "text-blue" : ""}`}
      to={to}
      onClick={() => setSelectedPage(lowerCasePage)}
    >
      <NavLabel page={page} />
    </RouterLink>
  );
};

const Navbar = ({ selectedPage, setSelectedPage }) => {
  const [hidden, setHidden] = useState(false);
  const aboveMediumScreens = useMediaQuery("(min-width: 1061px)");
  const { pathname } = useLocation();
  /* The navbar now mounts under the intro overlay along with the rest of the
     site, so its logo slide-in waits for the overlay to lift. */
  const revealed = useRevealed();

  // The navbar lives outside <Routes> and never remounts, so reveal it again
  // whenever a route change scrolls a fresh page back to the top.
  useEffect(() => {
    setHidden(false);
  }, [pathname]);

  useEffect(() => {
    let detachScroll;

    const unsubscribe = onLenisInstanceChange((lenis) => {
      if (detachScroll) {
        detachScroll();
        detachScroll = null;
      }
      if (!lenis) return;

      // virtual-scroll fires on raw wheel/touch delta (reacts instantly)
      const handleVirtualScroll = ({ deltaY }) => {
        if (deltaY < 0) {
          setHidden(false);
        } else if (deltaY > 0) {
          setHidden(true);
        }
      };

      detachScroll = lenis.on("virtual-scroll", handleVirtualScroll);
    });

    return () => {
      unsubscribe();
      if (detachScroll) detachScroll();
    };
  }, []);

  return (
    <>
      {aboveMediumScreens ? (
        <motion.nav
          variants={{
            visible: { y: 0, transition: { duration: 0.15, ease: "easeOut" } },
            hidden: {
              y: "-100%",
              transition: { duration: 0.4, ease: "easeInOut" },
            },
          }}
          className={`z-[60] w-full fixed top-0`}
          animate={hidden ? "hidden" : "visible"}
        >
          <motion.div
            className="bg-off-white border-b-2 border-semi-black flex items-center justify-between 
            py-1 mx-auto"
          >
            {/* LOGO */}
            <motion.img
              initial={{ x: -900 }}
              animate={revealed ? { x: 0 } : { x: -900 }}
              transition={{ duration: 1.5, ease: "easeInOut", delay: 0.8 }}
              alt="logo"
              src={logo}
              className="md:flex ml-16 w-[62px] h-[62px] "
            />
            {/*Desktop Nav*/}
            <div
              className="flex justify-between gap-4 text-base lg:text-[115%]
              py-2 pl-3 pr-6 rounded-full uppercase"
            >
              <Link
                page="Home"
                selectedPage={selectedPage}
                setSelectedPage={setSelectedPage}
              />
              <Link
                page="Projects"
                selectedPage={selectedPage}
                setSelectedPage={setSelectedPage}
              />
              <PageLink
                page="Blog"
                to="/blog"
                selectedPage={selectedPage}
                setSelectedPage={setSelectedPage}
              />
            </div>
          </motion.div>
        </motion.nav>
      ) : (
        /* MOBILE */
        <motion.nav
          variants={{
            visible: { y: 0, transition: { duration: 0.15, ease: "easeOut" } },
            hidden: {
              y: "-100%",
              transition: { duration: 0.4, ease: "easeInOut" },
            },
          }}
          className={`z-[60] w-full fixed top-0`}
          animate={hidden ? "hidden" : "visible"}
        >
          <motion.div
            className="bg-off-white border-b-2 border-semi-black flex items-center justify-between 
            py-1 mx-auto"
          >
            {/* LOGO */}
            <motion.img
              initial={{ x: -900 }}
              animate={revealed ? { x: 0 } : { x: -900 }}
              transition={{ duration: 1.5, ease: "easeInOut", delay: 0.8 }}
              alt="logo"
              src={logo}
              className="ml-3 w-[31px] h-[31px] "
            />
            {/*Desktop Nav*/}
            <div className="flex justify-between gap-2 text-sm px-3 uppercase">
              <Link
                page="Home"
                selectedPage={selectedPage}
                setSelectedPage={setSelectedPage}
              />
              <Link
                page="Projects"
                selectedPage={selectedPage}
                setSelectedPage={setSelectedPage}
              />
              <PageLink
                page="Blog"
                to="/blog"
                selectedPage={selectedPage}
                setSelectedPage={setSelectedPage}
              />
            </div>
          </motion.div>
        </motion.nav>
      )}
    </>
  );
};

export default Navbar;
